import React, { useState, useEffect, useRef } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import {
  SquaresFour, Users, Buildings, CheckSquare, FileText, CurrencyDollar, Scroll, ClipboardText,
  MagnifyingGlass, Plus, SignOut, List as ListIcon, X, BookOpen,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/command", end: true, label: "Dashboard", icon: SquaresFour, testid: "nav-dashboard" },
  { to: "/command/clients", label: "Clients", icon: Users, testid: "nav-clients" },
  { to: "/command/companies", label: "Companies", icon: Buildings, testid: "nav-companies" },
  { to: "/command/tasks", label: "Tasks", icon: CheckSquare, testid: "nav-tasks" },
  { to: "/command/billing", label: "Billing", icon: CurrencyDollar, testid: "nav-billing" },
  { to: "/command/letters", label: "Dispute Letters", icon: Scroll, testid: "nav-letters" },
  { to: "/command/approvals", label: "Approvals", icon: ClipboardText, testid: "nav-approvals" },
  { to: "/command/documents", label: "Documents", icon: FileText, testid: "nav-documents" },
];

const QUICK = [
  { label: "New Client", to: "/command/clients?new=1", testid: "quick-client" },
  { label: "New Company", to: "/command/companies?new=1", testid: "quick-company" },
  { label: "New Task", to: "/command/tasks?new=1", testid: "quick-task" },
  { label: "Log Payment", to: "/command/billing?new=payment", testid: "quick-payment" },
  { label: "New Document", to: "/command/documents?new=1", testid: "quick-document" },
];

const SLATT_EBOOK_URL = "https://customer-assets-7cd3h4nn.emergentagent.net/job_venture-launch-68/artifacts/6orabe1z_SLATT_EBOOK.pdf";

const NavItems = ({ onNavigate }) => (
  <nav className="flex flex-col gap-1">
    {NAV.map((n) => (
      <NavLink
        key={n.to}
        to={n.to}
        end={n.end}
        onClick={onNavigate}
        data-testid={n.testid}
        className={({ isActive }) =>
          `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors ${
            isActive ? "bg-primary/15 text-primary" : "text-zinc-400 hover:text-foreground hover:bg-white/5"
          }`
        }
      >
        <n.icon size={20} weight="duotone" />
        {n.label}
      </NavLink>
    ))}
    <a
      href={SLATT_EBOOK_URL}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onNavigate}
      data-testid="nav-slatt-ebook"
      className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors text-primary hover:bg-primary/10 mt-1"
    >
      <BookOpen size={20} weight="duotone" />
      SLATT eBook
    </a>
  </nav>
);

function GlobalSearch() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const boxRef = useRef(null);

  useEffect(() => {
    if (q.trim().length < 2) { setRes(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/cc/search", { params: { q } });
        setRes(data); setOpen(true);
      } catch { /* ignore */ }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const go = (path) => { setOpen(false); setQ(""); navigate(path); };
  const total = res ? res.clients.length + res.companies.length + res.tasks.length + res.documents.length : 0;

  return (
    <div className="relative flex-1 max-w-md" ref={boxRef}>
      <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
      <Input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => res && setOpen(true)}
        placeholder="Search clients, companies, tasks…"
        data-testid="global-search-input"
        className="pl-10 bg-zinc-900/60 border-white/10"
      />
      {open && res && (
        <div className="absolute z-50 mt-2 w-full rounded-xl border border-white/10 bg-zinc-950 shadow-2xl max-h-96 overflow-auto" data-testid="global-search-results">
          {total === 0 && <div className="p-4 text-sm text-muted-foreground">No matches</div>}
          {res.clients.map((c) => (
            <button key={c.id} onClick={() => go(`/command/clients/${c.id}`)} className="w-full text-left px-4 py-2.5 hover:bg-white/5 border-b border-white/5">
              <span className="text-xs text-primary uppercase mr-2">Client</span>{c.full_name || c.business_name}
            </button>
          ))}
          {res.companies.map((c) => (
            <button key={c.id} onClick={() => go(`/command/companies`)} className="w-full text-left px-4 py-2.5 hover:bg-white/5 border-b border-white/5">
              <span className="text-xs text-sky-300 uppercase mr-2">Company</span>{c.legal_name}
            </button>
          ))}
          {res.tasks.map((t) => (
            <button key={t.id} onClick={() => go(`/command/tasks`)} className="w-full text-left px-4 py-2.5 hover:bg-white/5 border-b border-white/5">
              <span className="text-xs text-amber-300 uppercase mr-2">Task</span>{t.name}
            </button>
          ))}
          {res.documents.map((d) => (
            <button key={d.id} onClick={() => go(`/command/documents`)} className="w-full text-left px-4 py-2.5 hover:bg-white/5 border-b border-white/5">
              <span className="text-xs text-zinc-300 uppercase mr-2">Doc</span>{d.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function CommandLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const doLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="min-h-screen bg-background text-foreground flex">
      {/* Sidebar (desktop) */}
      <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r border-white/10 bg-zinc-950 p-5 fixed inset-y-0">
        <Link to="/command" className="mb-8">
          <div className="flex items-center gap-2 mb-8">
            <img src="/logo.png" alt="DAFARGOS" className="h-9 w-9 object-contain rounded-lg" />
            <div className="font-display text-lg tracking-tighter">MASTER</div>
          </div>
          <div className="overline text-primary">Command Center</div>
        </Link>
        <NavItems />
        <div className="mt-auto pt-6 border-t border-white/10">
          <div className="text-xs text-muted-foreground truncate mb-2">{user?.email}</div>
          <button onClick={doLogout} data-testid="logout-button" className="flex items-center gap-2 text-sm text-zinc-400 hover:text-foreground">
            <SignOut size={18} /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMobileOpen(false)} />
          <aside className="relative w-64 bg-zinc-950 border-r border-white/10 p-5 flex flex-col">
            <button className="absolute top-4 right-4 text-muted-foreground" onClick={() => setMobileOpen(false)}><X size={20} /></button>
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-8">
            <img src="/logo.png" alt="DAFARGOS" className="h-9 w-9 object-contain rounded-lg" />
            <div className="font-display text-lg tracking-tighter">MASTER</div>
          </div>
              <div className="overline text-primary">Command Center</div>
            </div>
            <NavItems onNavigate={() => setMobileOpen(false)} />
            <button onClick={doLogout} className="mt-auto flex items-center gap-2 text-sm text-zinc-400 hover:text-foreground pt-6"><SignOut size={18} /> Sign out</button>
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 lg:ml-64 min-w-0">
        <header className="sticky top-0 z-40 border-b border-white/10 bg-zinc-950/70 backdrop-blur-xl">
          <div className="flex items-center gap-3 px-4 sm:px-6 h-16">
            <button className="lg:hidden text-muted-foreground" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-button"><ListIcon size={22} /></button>
            <GlobalSearch />
            <div className="ml-auto">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" data-testid="quick-add-button" className="bg-primary text-primary-foreground hover:bg-[#DBC197] active:scale-95 transition-transform gap-1">
                    <Plus size={18} weight="bold" /> <span className="hidden sm:inline">Add</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-zinc-950 border-white/10">
                  {QUICK.map((qa) => (
                    <DropdownMenuItem key={qa.to} data-testid={qa.testid} onClick={() => navigate(qa.to)} className="cursor-pointer">
                      {qa.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>
        <main className="px-4 sm:px-6 py-8 max-w-7xl">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
